// Pure value-summary leaf for a PO budget line's selected tags. Given the tag
// option lists (rate items / milestones / SOWs, each carrying an ex-GST value)
// and the ids selected on a line, it produces an ordered breakdown plus a
// total to guide the line total the user enters. Dependency-light (type-only
// import of TagOption) so it stays unit-testable and couples no vi.mock().
//
// Milestone (paymentAmount) and SOW (current value — original plus approved
// variation contributions) values are lump-sum totals;
// a rate item's value is a per-UNIT price. Per product decision the unit price
// is still included in the total, but flagged (includesUnitRate) so the UI can
// caveat the mixed basis. A null value means variable/unpriced: it contributes
// 0 and sets hasVariable.

import type { TagOption } from "./TagMultiSelect";

export interface SelectedValueLine {
  label: string;
  value: number | null;
  kind: "total" | "unitRate";
}

export interface SelectedValueSummary {
  items: SelectedValueLine[];
  total: number;
  includesUnitRate: boolean;
  hasVariable: boolean;
}

export function sumSelectedTagValues(
  options: TagOption[],
  selectedIds: string[],
): SelectedValueSummary {
  const selected = new Set(selectedIds);
  const items: SelectedValueLine[] = [];
  let total = 0;
  let includesUnitRate = false;
  let hasVariable = false;

  for (const opt of options) {
    if (!selected.has(opt.id)) continue;
    const kind = opt.valueKind ?? "total";
    const value = opt.value ?? null;
    items.push({ label: opt.label, value, kind });
    if (value === null) {
      hasVariable = true;
    } else {
      total += value;
      if (kind === "unitRate") includesUnitRate = true;
    }
  }

  return { items, total, includesUnitRate, hasVariable };
}
